import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { NewTenantAddressModel } from '../../../models/lab-manager/tenant-address';

/**
 * Types a new address for a customer.
 *
 * Closes with the values, and nothing is written until the caller posts them. The caller
 * then has to save the submission before the address is actually used, so adding one by
 * mistake changes nothing that prints.
 *
 * NO REQUIRED FIELDS. An address missing half its fields is a worse address, not an
 * invalid one, and a required field only pushes somebody into typing something false to
 * get past it. The API takes the same view.
 */
@Component({
  selector: 'app-add-address',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule],
  templateUrl: './add-address.component.html',
  styleUrls: ['./add-address.component.css']
})
export class AddAddressComponent {

  readonly form = new FormGroup({
    addressLine1: new FormControl<string | null>(null),
    addressLine2: new FormControl<string | null>(null),
    addressLine3: new FormControl<string | null>(null),
    town: new FormControl<string | null>(null),
    province: new FormControl<string | null>(null),
    postalCode: new FormControl<string | null>(null),
  });

  constructor(private dialogRef: MatDialogRef<AddAddressComponent>) {}

  /**
   * Whether there is anything worth saving.
   *
   * The one check made, and it is not about completeness: an address with every box
   * empty is not an address at all, and saving it would put an unlabelled row on the
   * customer that nobody can identify afterwards.
   */
  get hasAnything(): boolean {
    return Object.values(this.form.value).some(v => !!v && String(v).trim().length > 0);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  add(): void {
    if (!this.hasAnything) {
      return;
    }
    // Trimmed, so a field holding only spaces reaches the API as empty rather than as
    // content that then prints as a blank line on the lab sheet.
    const value = this.form.value;
    const trimmed: NewTenantAddressModel = {
      addressLine1: trim(value.addressLine1),
      addressLine2: trim(value.addressLine2),
      addressLine3: trim(value.addressLine3),
      town: trim(value.town),
      province: trim(value.province),
      postalCode: trim(value.postalCode),
    };
    this.dialogRef.close(trimmed);
  }
}

function trim(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length ? trimmed : null;
}
